import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../App';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  FaCrown, FaRocket, FaBuilding, FaHandshake, FaUsers, 
  FaSearch, FaEdit, FaEuroSign, FaCalendar, FaChartBar
} from 'react-icons/fa';

export default function SubscriptionManagement() {
  const { API } = useContext(AppContext);
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editPlan, setEditPlan] = useState('');
  const [editStatus, setEditStatus] = useState('');
  
  // Custom plan creation
  const [creatingCustomPlan, setCreatingCustomPlan] = useState(null);
  const [customPlanConfig, setCustomPlanConfig] = useState({
    max_product_ads: 10,
    max_rental_ads: 10,
    max_request_ads: 5,
    max_directory_ads: 1,
    can_create_chantier: true,
    has_supplier_shop: true,
    priority_on_requests: true,
    price_paid: 0,
    duration_months: 12,
    notes: ''
  });
  
  // Edit custom subscription
  const [editingCustomSub, setEditingCustomSub] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    trial: 0,
    active: 0,
    expired: 0,
    byPlan: {}
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterPlan]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch subscriptions
      let url = `${API}/subscriptions/admin/subscriptions?limit=200`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterPlan) url += `&plan=${filterPlan}`;
      
      const [subsRes, transRes, contactRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/subscriptions/admin/transactions?limit=100`),
        axios.get(`${API}/subscriptions/admin/contact-requests?limit=100`)
      ]);
      
      setSubscriptions(subsRes.data);
      setTransactions(transRes.data);
      setContactRequests(contactRes.data);
      
      // Calculate stats
      const allSubs = subsRes.data;
      const newStats = {
        total: allSubs.length,
        trial: allSubs.filter(s => s.status === 'trial').length,
        active: allSubs.filter(s => s.status === 'active').length,
        expired: allSubs.filter(s => s.status === 'expired').length,
        byPlan: {}
      };
      
      allSubs.forEach(s => {
        newStats.byPlan[s.plan] = (newStats.byPlan[s.plan] || 0) + 1;
      });
      
      setStats(newStats);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!editingUser || !editPlan || !editStatus) return;

    try {
      await axios.put(
        `${API}/subscriptions/admin/subscriptions/${editingUser.user_id}?plan=${editPlan}&status=${editStatus}`
      );
      toast.success('Abonnement mis à jour !');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getPlanIcon = (plan) => {
    const icons = {
      'decouverte': <FaRocket className="text-blue-500" />,
      'pro': <FaCrown className="text-yellow-500" />,
      'fournisseur': <FaBuilding className="text-[#635BFF]" />,
      'sur_mesure': <FaHandshake className="text-purple-500" />
    };
    return icons[plan] || <FaRocket className="text-gray-500" />;
  };

  const getPlanName = (plan) => {
    const names = {
      'decouverte': 'Découverte',
      'pro': 'Pro',
      'fournisseur': 'Fournisseur',
      'sur_mesure': 'Sur Mesure'
    };
    return names[plan] || plan;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'trial': { text: 'Essai', class: 'bg-blue-500' },
      'active': { text: 'Actif', class: 'bg-[#635BFF]' },
      'expired': { text: 'Expiré', class: 'bg-red-500' },
      'cancelled': { text: 'Annulé', class: 'bg-gray-500' }
    };
    const badge = badges[status] || { text: status, class: 'bg-gray-500' };
    return <Badge className={badge.class}>{badge.text}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      sub.user_name?.toLowerCase().includes(term) ||
      sub.user_email?.toLowerCase().includes(term)
    );
  });

  if (loading && subscriptions.length === 0) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
          <div className="text-xl text-gray-600">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#0A2540] mb-2 flex items-center gap-3">
              <FaCrown className="text-yellow-500" />
              Gestion des Abonnements
            </h1>
            <p className="text-gray-600">
              Gérez les abonnements et paiements des utilisateurs
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 text-center">
                <FaUsers className="text-2xl text-[#0A2540] mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="w-4 h-4 bg-blue-500 rounded-full mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-600">{stats.trial}</p>
                <p className="text-xs text-gray-500">En essai</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="w-4 h-4 bg-[#635BFF] rounded-full mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#635BFF]">{stats.active}</p>
                <p className="text-xs text-gray-500">Actifs</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="w-4 h-4 bg-red-500 rounded-full mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
                <p className="text-xs text-gray-500">Expirés</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <FaRocket className="text-2xl text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.byPlan['decouverte'] || 0}</p>
                <p className="text-xs text-gray-500">Découverte</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <FaCrown className="text-2xl text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.byPlan['pro'] || 0}</p>
                <p className="text-xs text-gray-500">Pro</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'subscriptions' ? 'default' : 'outline'}
              onClick={() => setActiveTab('subscriptions')}
              className={activeTab === 'subscriptions' ? 'bg-[#0A2540]' : ''}
            >
              <FaUsers className="mr-2" />
              Abonnements
            </Button>
            <Button
              variant={activeTab === 'transactions' ? 'default' : 'outline'}
              onClick={() => setActiveTab('transactions')}
              className={activeTab === 'transactions' ? 'bg-[#0A2540]' : ''}
            >
              <FaEuroSign className="mr-2" />
              Paiements
            </Button>
            <Button
              variant={activeTab === 'contacts' ? 'default' : 'outline'}
              onClick={() => setActiveTab('contacts')}
              className={activeTab === 'contacts' ? 'bg-[#0A2540]' : ''}
            >
              <FaHandshake className="mr-2" />
              Demandes Sur Mesure
              {contactRequests.filter(c => c.status === 'pending').length > 0 && (
                <Badge className="ml-2 bg-red-500">{contactRequests.filter(c => c.status === 'pending').length}</Badge>
              )}
            </Button>
          </div>

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle>Liste des Abonnements</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <div className="relative">
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-48"
                      />
                    </div>
                    <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="trial">Essai</SelectItem>
                        <SelectItem value="active">Actif</SelectItem>
                        <SelectItem value="expired">Expiré</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterPlan || "all"} onValueChange={(v) => setFilterPlan(v === "all" ? "" : v)}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Plan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="decouverte">Découverte</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="fournisseur">Fournisseur</SelectItem>
                        <SelectItem value="sur_mesure">Sur Mesure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-3 px-4">Utilisateur</th>
                        <th className="py-3 px-4">Plan</th>
                        <th className="py-3 px-4">Statut</th>
                        <th className="py-3 px-4">Annonces</th>
                        <th className="py-3 px-4">Expiration</th>
                        <th className="py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscriptions.map((sub) => (
                        <tr key={sub.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{sub.user_name}</p>
                              <p className="text-xs text-gray-500">{sub.user_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {getPlanIcon(sub.plan)}
                              <span>{getPlanName(sub.plan)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {getStatusBadge(sub.status)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-xs">
                              <span>P:{sub.ads_count?.product || 0}</span>
                              <span className="mx-1">|</span>
                              <span>L:{sub.ads_count?.rental || 0}</span>
                              <span className="mx-1">|</span>
                              <span>D:{sub.ads_count?.request || 0}</span>
                              <span className="mx-1">|</span>
                              <span>A:{sub.ads_count?.directory || 0}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {sub.status === 'trial' 
                              ? formatDate(sub.trial_end)
                              : formatDate(sub.subscription_end)
                            }
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingUser(sub);
                                  setEditPlan(sub.plan);
                                  setEditStatus(sub.status);
                                }}
                              >
                                <FaEdit className="mr-1" />
                                Modifier
                              </Button>
                              {sub.plan === 'sur_mesure' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-purple-600 border-purple-600"
                                  onClick={() => {
                                    const limits = sub.custom_limits || {};
                                    setEditingCustomSub(sub);
                                    setCustomPlanConfig({
                                      max_product_ads: limits.max_product_ads || 10,
                                      max_rental_ads: limits.max_rental_ads || 10,
                                      max_request_ads: limits.max_request_ads || 5,
                                      max_directory_ads: limits.max_directory_ads || 1,
                                      can_create_chantier: limits.can_create_chantier !== false,
                                      has_supplier_shop: limits.has_supplier_shop !== false,
                                      priority_on_requests: limits.priority_on_requests !== false,
                                      price_paid: limits.price_paid || 0,
                                      duration_months: 12,
                                      notes: limits.notes || ''
                                    });
                                  }}
                                >
                                  🎯
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-600 hover:bg-red-50"
                                onClick={() => setDeleteConfirm(sub)}
                              >
                                🗑️
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {filteredSubscriptions.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Aucun abonnement trouvé
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <Card>
              <CardHeader>
                <CardTitle>Historique des Paiements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-3 px-4">Date</th>
                        <th className="py-3 px-4">Utilisateur</th>
                        <th className="py-3 px-4">Plan</th>
                        <th className="py-3 px-4">Montant</th>
                        <th className="py-3 px-4">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm">
                            {formatDate(tx.created_at)}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {tx.user_id}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {getPlanIcon(tx.plan)}
                              <span>{getPlanName(tx.plan)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-bold">
                            {tx.amount}€
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={
                              tx.status === 'paid' ? 'bg-[#635BFF]' :
                              tx.status === 'pending' ? 'bg-yellow-500' :
                              'bg-red-500'
                            }>
                              {tx.status === 'paid' ? 'Payé' :
                               tx.status === 'pending' ? 'En attente' :
                               'Échoué'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {transactions.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Aucun paiement enregistré
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact Requests Tab */}
          {activeTab === 'contacts' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FaHandshake className="text-purple-500" />
                  Demandes Sur Mesure
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contactRequests.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Aucune demande sur mesure
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contactRequests.map((req) => (
                      <div 
                        key={req.id}
                        className={`border rounded-lg p-4 ${req.status === 'pending' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-lg">{req.name}</h3>
                            <p className="text-sm text-gray-600">{req.email}</p>
                            {req.phone && <p className="text-sm text-gray-600">{req.phone}</p>}
                            {req.company && <p className="text-sm text-gray-500">Entreprise: {req.company}</p>}
                          </div>
                          <div className="text-right">
                            <Badge className={
                              req.status === 'pending' ? 'bg-yellow-500' :
                              req.status === 'contacted' ? 'bg-blue-500' :
                              req.status === 'converted' ? 'bg-[#635BFF]' :
                              'bg-gray-500'
                            }>
                              {req.status === 'pending' ? '⏳ En attente' :
                               req.status === 'contacted' ? '📞 Contacté' :
                               req.status === 'converted' ? '✅ Converti' :
                               req.status}
                            </Badge>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(req.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded p-3 mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Message :</p>
                          <p className="text-sm text-gray-600">{req.message}</p>
                        </div>
                        
                        {(req.status === 'pending' || req.status === 'contacted') && (
                          <div className="flex flex-wrap gap-2">
                            {req.status === 'pending' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/subscriptions/admin/contact-requests/${req.id}?status=contacted`);
                                    toast.success('Marqué comme contacté');
                                    fetchData();
                                  } catch (e) {
                                    toast.error('Erreur');
                                  }
                                }}
                              >
                                📞 Marquer contacté
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              className="bg-purple-600 hover:bg-purple-700"
                              onClick={() => {
                                setCreatingCustomPlan(req);
                                setCustomPlanConfig({
                                  max_product_ads: 10,
                                  max_rental_ads: 10,
                                  max_request_ads: 5,
                                  max_directory_ads: 1,
                                  can_create_chantier: true,
                                  has_supplier_shop: true,
                                  priority_on_requests: true,
                                  price_paid: 0,
                                  duration_months: 12,
                                  notes: ''
                                });
                              }}
                            >
                              🎯 Créer abonnement sur mesure
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-[#635BFF] hover:bg-[#5248d9]"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/subscriptions/admin/contact-requests/${req.id}?status=converted`);
                                  toast.success('Marqué comme converti');
                                  fetchData();
                                } catch (e) {
                                  toast.error('Erreur');
                                }
                              }}
                            >
                              ✅ Converti (déjà payé)
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Custom Plan Creation Modal */}
          {creatingCustomPlan && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <Card className="w-full max-w-2xl my-8">
                <CardHeader className="bg-purple-50">
                  <CardTitle className="flex items-center gap-2">
                    <FaHandshake className="text-purple-500" />
                    Créer un Abonnement Sur Mesure
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {/* Client Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-bold mb-2">Client</h3>
                      <p><strong>Nom :</strong> {creatingCustomPlan.name}</p>
                      <p><strong>Email :</strong> {creatingCustomPlan.email}</p>
                      {creatingCustomPlan.phone && <p><strong>Tél :</strong> {creatingCustomPlan.phone}</p>}
                      {creatingCustomPlan.company && <p><strong>Entreprise :</strong> {creatingCustomPlan.company}</p>}
                    </div>

                    {/* Limits Configuration */}
                    <div>
                      <h3 className="font-bold mb-3">📊 Limites d'Annonces</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label>Produits max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_product_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_product_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Locations max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_rental_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_rental_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Demandes max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_request_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_request_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Annuaire max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_directory_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_directory_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div>
                      <h3 className="font-bold mb-3">✨ Fonctionnalités</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={customPlanConfig.can_create_chantier}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, can_create_chantier: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <span>Listes de chantier</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={customPlanConfig.has_supplier_shop}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, has_supplier_shop: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <span>Boutique fournisseur</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={customPlanConfig.priority_on_requests}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, priority_on_requests: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <span>Priorité sur demandes</span>
                        </label>
                      </div>
                    </div>

                    {/* Pricing & Duration */}
                    <div>
                      <h3 className="font-bold mb-3">💰 Facturation</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Prix payé (€)</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.price_paid}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, price_paid: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Durée (mois)</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.duration_months}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, duration_months: parseInt(e.target.value) || 12})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <Label>Notes internes</Label>
                      <Input 
                        value={customPlanConfig.notes}
                        onChange={(e) => setCustomPlanConfig({...customPlanConfig, notes: e.target.value})}
                        placeholder="Notes sur cet abonnement..."
                      />
                    </div>

                    {/* Summary */}
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-bold mb-2">📋 Résumé</h3>
                      <p className="text-sm">
                        <strong>{creatingCustomPlan.email}</strong> aura accès à : 
                        {customPlanConfig.max_product_ads} produits, 
                        {customPlanConfig.max_rental_ads} locations, 
                        {customPlanConfig.max_request_ads} demandes, 
                        {customPlanConfig.max_directory_ads} entrée(s) annuaire
                        {customPlanConfig.can_create_chantier && ', listes de chantier'}
                        {customPlanConfig.has_supplier_shop && ', boutique fournisseur'}
                        {customPlanConfig.priority_on_requests && ', priorité sur demandes'}
                        {' '}pendant {customPlanConfig.duration_months} mois
                        {customPlanConfig.price_paid > 0 && ` pour ${customPlanConfig.price_paid}€`}.
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button 
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={async () => {
                          try {
                            const params = new URLSearchParams({
                              user_email: creatingCustomPlan.email,
                              ...customPlanConfig
                            });
                            const response = await axios.post(`${API}/subscriptions/admin/create-custom-subscription?${params}`);
                            
                            // Mark contact request as converted
                            await axios.put(`${API}/subscriptions/admin/contact-requests/${creatingCustomPlan.id}?status=converted`);
                            
                            // Show appropriate message based on response
                            if (response.data.status === 'pending') {
                              toast.success(response.data.message_fr || `Abonnement en attente créé pour ${creatingCustomPlan.email}. Il sera activé lors de l'inscription.`, {
                                duration: 6000
                              });
                            } else {
                              toast.success(`Abonnement sur mesure créé pour ${creatingCustomPlan.email} !`);
                            }
                            
                            setCreatingCustomPlan(null);
                            fetchData();
                          } catch (e) {
                            console.error(e);
                            toast.error(e.response?.data?.detail || 'Erreur lors de la création');
                          }
                        }}
                      >
                        ✅ Créer l'abonnement
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setCreatingCustomPlan(null)}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Edit Modal */}
          {editingUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle>Modifier l'abonnement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Utilisateur</p>
                      <p className="font-bold">{editingUser.user_name}</p>
                      <p className="text-sm text-gray-500">{editingUser.user_email}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Plan</label>
                      <Select value={editPlan} onValueChange={setEditPlan}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="decouverte">Découverte (99€/mois)</SelectItem>
                          <SelectItem value="pro">Pro (1500€/an)</SelectItem>
                          <SelectItem value="fournisseur">Fournisseur (2000€/an)</SelectItem>
                          <SelectItem value="sur_mesure">Sur Mesure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Statut</label>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="trial">Essai (7 jours)</SelectItem>
                          <SelectItem value="active">Actif (1 an)</SelectItem>
                          <SelectItem value="expired">Expiré</SelectItem>
                          <SelectItem value="cancelled">Annulé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                      <p className="font-medium text-yellow-800">⚠️ Attention</p>
                      <p className="text-yellow-700">
                        Changer le statut en "Actif" donnera accès pendant 1 an.
                        Les compteurs d'annonces seront remis à zéro.
                      </p>
                    </div>
                    
                    <div className="flex gap-3">
                      <Button 
                        className="flex-1 bg-[#0A2540]"
                        onClick={handleUpdateSubscription}
                      >
                        Enregistrer
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setEditingUser(null)}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Edit Custom Subscription Modal */}
          {editingCustomSub && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <Card className="w-full max-w-2xl my-8">
                <CardHeader className="bg-purple-50">
                  <CardTitle className="flex items-center gap-2">
                    <FaEdit className="text-purple-500" />
                    Modifier l'Abonnement Sur Mesure
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {/* Client Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-bold mb-2">Client</h3>
                      <p><strong>Nom :</strong> {editingCustomSub.user_name}</p>
                      <p><strong>Email :</strong> {editingCustomSub.user_email}</p>
                    </div>

                    {/* Limits Configuration */}
                    <div>
                      <h3 className="font-bold mb-3">📊 Limites d'Annonces</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label>Produits max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_product_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_product_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Locations max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_rental_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_rental_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Demandes max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_request_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_request_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Annuaire max</Label>
                          <Input 
                            type="number" 
                            value={customPlanConfig.max_directory_ads}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, max_directory_ads: parseInt(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div>
                      <h3 className="font-bold mb-3">✨ Fonctionnalités</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={customPlanConfig.can_create_chantier}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, can_create_chantier: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <span>Listes de chantier</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={customPlanConfig.has_supplier_shop}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, has_supplier_shop: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <span>Boutique fournisseur</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={customPlanConfig.priority_on_requests}
                            onChange={(e) => setCustomPlanConfig({...customPlanConfig, priority_on_requests: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <span>Priorité sur demandes</span>
                        </label>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Prolonger de (mois)</Label>
                        <Input 
                          type="number" 
                          value={customPlanConfig.duration_months}
                          onChange={(e) => setCustomPlanConfig({...customPlanConfig, duration_months: parseInt(e.target.value) || 12})}
                        />
                        <p className="text-xs text-gray-500 mt-1">Laisser vide pour ne pas modifier la durée</p>
                      </div>
                      <div>
                        <Label>Notes internes</Label>
                        <Input 
                          value={customPlanConfig.notes}
                          onChange={(e) => setCustomPlanConfig({...customPlanConfig, notes: e.target.value})}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button 
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={async () => {
                          try {
                            const params = new URLSearchParams(customPlanConfig);
                            await axios.put(`${API}/subscriptions/admin/update-custom-subscription/${editingCustomSub.user_id}?${params}`);
                            toast.success('Abonnement mis à jour !');
                            setEditingCustomSub(null);
                            fetchData();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Erreur');
                          }
                        }}
                      >
                        💾 Enregistrer les modifications
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setEditingCustomSub(null)}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader className="bg-red-50">
                  <CardTitle className="text-red-600">⚠️ Supprimer l'abonnement</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="mb-4">
                    Êtes-vous sûr de vouloir supprimer l'abonnement de <strong>{deleteConfirm.user_name}</strong> ({deleteConfirm.user_email}) ?
                  </p>
                  <p className="text-sm text-gray-600 mb-6">
                    L'utilisateur perdra tous ses avantages et devra souscrire à nouveau.
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      variant="destructive"
                      className="flex-1"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/subscriptions/admin/subscriptions/${deleteConfirm.user_id}`);
                          toast.success('Abonnement supprimé');
                          setDeleteConfirm(null);
                          fetchData();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Erreur');
                        }
                      }}
                    >
                      🗑️ Supprimer
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setDeleteConfirm(null)}
                    >
                      Annuler
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
